import { faComment } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { LocationDescriptor } from 'history';
import {
    counter_RATINGS,
    BZ2_ESHOP_PRODUCT_SHORT,
    BZ2_NEW_PRODUCT_HOVER,
    BZ2_OLD_PRODUCT_HOVER,
    FROM,
    PRODUCT_BOX_MARKET_BUTTON_LABEL,
    PRODUCT_BOX_WRITE_REVIEW, WIKI_WRITE_YOUR_FIRST_EXPERIENCE,
} from 'mk/autogenerated/translations/ProductBox.fb60c931b47e7ee39040b333ee8fd8fd'
import { formatPrice } from 'mk/bazaar/common/formatters';
import { strollersReviewsUrl } from 'mk2/apps/strollers/urls';
import { Btn, BtnType } from 'mk2/components/Btn';
import { Dot } from 'mk2/components/Dot';
import { Link } from 'mk2/components/Link';
import { LoadingPlaceholder } from 'mk2/components/LoadingPlaceholder';
import { Panel } from 'mk2/components/Panel';
import { StarsRating } from 'mk2/components/StarsRating';
import { url } from 'mk2/helpers/urls';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';
import styles from './ProductBox.mscss';

interface OwnProps {
    className?: string;
    style?: React.CSSProperties;
    image: React.ReactNode;
    title: React.ReactNode;
    reviewsCount: number;
    score: number;
    scoreOf?: number;
    strollerSlug?: string;

    productsTo?: LocationDescriptor;
    productsCount?: number;
    addReviewTo?: LocationDescriptor;

    // New products section
    newProductsMinPrice?: number | string;
    newProductsMaxPrice?: number | string;

    // Old products section
    oldProductsMinPrice?: number | string;
    oldProductsMaxPrice?: number | string;

    // Eshop products section
    eshopProductsMinPrice?: number | string;
    eshopProductsMaxPrice?: number | string;
}

type Props = OwnProps;

export const ProductBox: React.FunctionComponent<Props> = React.memo(({
    className,
    style,
    image,
    title,
    strollerSlug,
    reviewsCount,
    score,
    scoreOf = 5,
    productsTo,
    productsCount,
    eshopProductsMinPrice,
    eshopProductsMaxPrice,
    newProductsMaxPrice,
    newProductsMinPrice,
    oldProductsMaxPrice,
    oldProductsMinPrice,
    addReviewTo,
}) => {
    score = score ?? 0;
    return (
        <Panel className={cx(styles.ProductBox, className)} style={style}>
            <div className={styles.ProductBox__image}>{image}</div>
            <div className={styles.ProductBox__content}>
                <div className={styles.ProductBox__content__title}>{title}</div>
                <div className={styles.ProductBox__content__score}>
                    <StarsRating
                        starsCount={
                            // <StarsRating> is using 5 stars
                            // so recalculate score if scoreOf is not 5
                            scoreOf !== 5 ? (score / scoreOf) * 5 : score
                        }
                        className={styles.ProductBox__content__score__stars}
                        canShowHalf
                    />
                    <Dot spaces={1} className={styles.ProductBox__content__score__dot} />
                    <span className={styles.ProductBox__content__score__reviews}>
                       {strollerSlug ? (
                            <Link to={url(strollersReviewsUrl, { slug: strollerSlug }) }>
                                {interpolate(counter_RATINGS, { counter: reviewsCount, count: reviewsCount })}{' '}
                                <FontAwesomeIcon icon={faComment} />
                            </Link>
                       ) : (
                            <>
                                {interpolate(counter_RATINGS, { counter: reviewsCount, count: reviewsCount })}{' '}
                                <FontAwesomeIcon icon={faComment} />
                            </>
                       )}
                    </span>
                </div>
                {productsCount > 0 ? (
                    <>
                        <div className={styles.ProductBox__content__products}>
                            {(eshopProductsMinPrice || eshopProductsMaxPrice) && (
                                <div className={styles.ProductBox__content__products__eshop}>
                                    <div className={styles.ProductBox__content__products__eshop__badge}>
                                        <span className={styles.ProductBox__content__products__eshop__badge__badge}>
                                            {BZ2_ESHOP_PRODUCT_SHORT}
                                        </span>
                                    </div>
                                    {eshopProductsMinPrice && (
                                        <div className={styles.ProductBox__content__products__eshop__price}>
                                            {eshopProductsMaxPrice ? (
                                                <>
                                                    {formatPrice(eshopProductsMinPrice)} -{' '}
                                                    {formatPrice(eshopProductsMaxPrice)}
                                                </>
                                            ) : (
                                                <>
                                                    {FROM} {formatPrice(eshopProductsMinPrice)}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {(newProductsMinPrice || newProductsMaxPrice) && (
                                <div className={styles.ProductBox__content__products__new}>
                                    <div className={styles.ProductBox__content__products__new__badge}>
                                        <span className={styles.ProductBox__content__products__new__badge__badge}>
                                            {BZ2_NEW_PRODUCT_HOVER}
                                        </span>
                                    </div>
                                    {newProductsMinPrice && (
                                        <div className={styles.ProductBox__content__products__new__price}>
                                            {newProductsMaxPrice ? (
                                                <>
                                                    {formatPrice(newProductsMinPrice)} -{' '}
                                                    {formatPrice(newProductsMaxPrice)}
                                                </>
                                            ) : (
                                                <>
                                                    {FROM} {formatPrice(newProductsMinPrice)}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            {(oldProductsMaxPrice || oldProductsMinPrice) && (
                                <div className={styles.ProductBox__content__products__old}>
                                    <div className={styles.ProductBox__content__products__old__badge}>
                                        <span className={styles.ProductBox__content__products__old__badge__badge}>
                                            {BZ2_OLD_PRODUCT_HOVER}
                                        </span>
                                    </div>
                                    {oldProductsMinPrice && (
                                        <div className={styles.ProductBox__content__products__old__price}>
                                            {oldProductsMaxPrice ? (
                                                <>
                                                    {formatPrice(oldProductsMinPrice)} -{' '}
                                                    {formatPrice(oldProductsMaxPrice)}
                                                </>
                                            ) : (
                                                <>
                                                    {FROM} {formatPrice(oldProductsMinPrice)}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={styles.ProductBox__content__button}>
                            <Btn
                                className={styles.ProductBox__content__button__button}
                                link={productsTo}
                                type={BtnType.Blue}
                                label={
                                    <>
                                        {PRODUCT_BOX_MARKET_BUTTON_LABEL} {productsCount > 0 && <>({productsCount})</>}
                                    </>
                                }
                            />
                        </div>
                    </>
                ) : addReviewTo ? (
                    <div className={styles.ProductBox__content__review}>
                        <Link to={addReviewTo}>
                            {reviewsCount > 0 ? PRODUCT_BOX_WRITE_REVIEW : WIKI_WRITE_YOUR_FIRST_EXPERIENCE}
                        </Link>
                    </div>
                ) : null}
            </div>
        </Panel>
    );
});

ProductBox.displayName = 'ProductBox';

interface PlaceholderOwnProps {
    className?: string;
    style?: React.CSSProperties;
}

export const ProductBoxPlaceholder: React.FunctionComponent<PlaceholderOwnProps> = ({ className, style }) => (
    <Panel className={cx(styles.ProductBox, className)} style={style}>
        <div className={styles.ProductBox__image}>
            <LoadingPlaceholder width={64} height={64} />
        </div>
        <div className={styles.ProductBox__content}>
            <div className={styles.ProductBox__content__title}>
                <LoadingPlaceholder width={122} />
            </div>
            <div className={styles.ProductBox__content__score}>
                <LoadingPlaceholder width={250} height={19} />
            </div>
        </div>
    </Panel>
);

ProductBoxPlaceholder.displayName = 'ProductBoxPlaceholder';
